import type { FC } from "react";
import { useState } from "react";

import clsx from "clsx";

import type { ILocation } from "@/src/contexts/LocationContext";
import { useLocationContext } from "@/src/contexts/LocationContext";
import type { IMultipleSelect } from "@Components/MultipleSelect";

import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import type { IShoppingMallsFiltersHeader } from "./ShoppingMallsFiltersHeader";
import { ShoppingMallsFiltersHeader } from "./ShoppingMallsFiltersHeader";
import type { IShoppingMallsFiltersListsMap } from "./ShoppingMallsFiltersListsMap";
import { ShoppingMallsFiltersListsMap } from "./ShoppingMallsFiltersListsMap";
import type { IKeyValueOption } from "./ShoppingMallsFiltersMap";
import type { IShoppingMallsFiltersSearch } from "./ShoppingMallsFiltersSearch";
import { ShoppingMallsFiltersSearch } from "./ShoppingMallsFiltersSearch";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./ShoppingMallsFilters.module.css";

export interface IShoppingMallsFilters {
    header: IShoppingMallsFiltersHeader;
    search: IShoppingMallsFiltersSearch;
    filtersListsMap: IShoppingMallsFiltersListsMap;
    additionalServiceList: IKeyValueOption[];
    searchBrands: IMultipleSelect;
    selectedServicesList?: IKeyValueOption[];
    selectedBrandsList?: IKeyValueOption[];
    contentContainers?: IContentContainer;
}

interface IShoppingMallsFiltersState {
    selectedServicesList: IKeyValueOption[];
    selectedBrandsList: IKeyValueOption[];
    filtersListsMap: IShoppingMallsFiltersListsMap;
    location: ILocation;
}

const ShoppingMallsFilters: FC<IShoppingMallsFilters> = (props) => {
    const [mapAction] = useState<string>(props.filtersListsMap.mapComponent.ajaxAction);
    const { location } = useLocationContext();

    const [state, setState] = useState<IShoppingMallsFiltersState>({
        selectedServicesList: [...props.selectedServicesList],
        selectedBrandsList: [...props.selectedBrandsList],
        filtersListsMap: { ...props.filtersListsMap },
        location,
    });

    const renderSearch = () => {
        if (!props.search) {
            return null;
        }

        const serviceChange = (serviceList: IKeyValueOption[]) => {
            setState((currState) => ({ ...currState, selectedServicesList: serviceList }));

            return false;
        };

        const brandChange = (brandsList: IKeyValueOption[]) => {
            setState((currState) => ({ ...currState, selectedBrandsList: brandsList }));
        };

        const search = { ...props.search };
        search.additionalServiceList = props.additionalServiceList;
        search.searchBrands = props.searchBrands;
        search.onServiceChange = serviceChange;
        search.onBrandChange = brandChange;

        search.selectedServicesList = state.selectedServicesList;
        search.selectedBrandsList = state.selectedBrandsList;

        return <ShoppingMallsFiltersSearch {...search} />;
    };

    const renderFiltersListsMap = () => {
        if (!state.filtersListsMap) {
            return null;
        }

        const filtersListsMap = { ...state.filtersListsMap };

        const parseParams = (paramName: string, array: string[]) => {
            const url = `${paramName}=`;
            return url + array.join(`&${paramName}=`);
        };

        filtersListsMap.mapComponent.ajaxAction = `${mapAction}?${parseParams(
            "servicesNames",
            state.selectedServicesList.map((service) => service.value),
        )}&${parseParams(
            "brandsNames",
            state.selectedBrandsList.map((brand) => brand.value),
        )}`;

        filtersListsMap.additionalServiceList = props.additionalServiceList;
        filtersListsMap.brandsList = props.searchBrands.options;
        filtersListsMap.selectedServices = state.selectedServicesList.slice();
        filtersListsMap.selectedBrands = state.selectedBrandsList.slice();
        filtersListsMap.location = state.location;

        return <ShoppingMallsFiltersListsMap {...filtersListsMap} />;
    };

    const header = {
        ...props.header,
        placeDescription: state.location ? state.location.placeDescription : null,
    };

    return (
        <div className={clsx(css.shoppingMallsFiltersComponent, cssPortal.standardContainer)}>
            <ShoppingMallsFiltersHeader {...header} />
            {renderSearch()}
            {renderFiltersListsMap()}
            <ContentContainer {...props.contentContainers} />
        </div>
    );
};

export { ShoppingMallsFilters };
